import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useUserContext } from '../context/UserContext';
import { useAnalytics } from '../context/AnalyticsContext';
import { useNotifications } from '../context/NotificationContext';
import './Profile.css';
import { useChangePassword, useDeleteAccount, useUpdateUser } from '../api/users';

const Profile: React.FC = () => {
  const { user, updateUser, logout } = useUserContext();
  const { trackPageView } = useAnalytics();
  const { showSuccess, showError } = useNotifications();
  const changePasswordMutation = useChangePassword();
  const deleteAccountMutation = useDeleteAccount();
  const updateUserMutation = useUpdateUser();
  const [fullName, setFullName] = useState(user?.name || '');
  const [newPassword, setNewPassword] = useState('');

  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  React.useEffect(() => {
    trackPageView('/profile', 'Profile Settings');
  }, [trackPageView]);

  React.useEffect(() => {
    setFullName(user?.name || '');
  }, [user]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);

    try {
      const response = await updateUserMutation.mutateAsync({
        name: fullName
      });
      updateUser(response);
      showSuccess('Profile updated successfully');
    } catch (error: any) {
      showError('Failed to update profile. Please try again.', error);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsChangingPassword(true);

    try {
      await changePasswordMutation.mutateAsync({
        password: newPassword
      });
      showSuccess('Password changed successfully');
    } catch (error: any) {
      showError('Failed to change password. Please check your current password and try again.', error);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }

    const confirmText = window.prompt('Type "DELETE" to confirm account deletion:');
    if (confirmText !== 'DELETE') {
      showError('Account deletion cancelled', new Error('Account deletion cancelled'));
      return;
    }

    setIsDeletingAccount(true);

    try {
      await deleteAccountMutation.mutateAsync();
      showSuccess('Account deleted successfully');
      // Redirect will be handled by UserContext
    } catch (error: any) {
      showError('Failed to delete account. Please try again.', error);
    } finally {
      setIsDeletingAccount(false);
      logout();
      window.location.href = '/';
    }
  };

  return (
    <div className="profile-page">
      <Header />
      
      <main className="profile-main">
        <div className="profile-container">
          <h1 className="profile-title">Account Settings</h1>

          {/* Profile Information Section */}
          <div className="profile-section">
            <div className="profile-section-header">
              <h2 className="profile-section-title">Profile Information</h2>
              <p className="profile-section-subtitle">Update your personal details and account settings.</p>
            </div>
            
            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-group">
                <label htmlFor="fullName" className="form-label">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="form-input"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="btn btn--primary btn--save"
                disabled={isUpdatingProfile}
              >
                {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>

          {/* Password Management Section */}
          <div className="profile-section">
            <div className="profile-section-header">
              <h2 className="profile-section-title">Password Management</h2>
              <p className="profile-section-subtitle">Change your password by typing your new password twice.</p>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="profile-form">              
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-input"
                  placeholder="Confirm your new password"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="btn btn--primary btn--save"
                disabled={isChangingPassword}
              >
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>

          {/* Account Deletion Section */}
          <div className="profile-section profile-section--danger">
            <p className="delete-account-text">
              If you would like to remove your account entirely from our database, please{' '}
              <button
                type="button"
                className="delete-account-link"
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
              >
                click here
              </button>
              .
            </p>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Profile;
